import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Alert,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { StyledDataGrid } from '../../../DataGrid/DataGridStyles';
import { AutosizeColumnsToggle } from '../../../DataGrid/AutosizeColumnsToggle';
import { usePositionsMonitoringStep } from './usePositionsMonitoringStep';
import { getColumns, columnGroupingModel } from './columns';
import { PositionsMonitoringStepProps } from './types';
import { StepMetadata } from '../../../../pages/DataFeedWizard/types';
import { SearchBar } from '../../../SearchBar/SearchBar';
import { SummaryChips } from './SummaryChips';
import './PositionsMonitoringStep.scss';

// Step metadata for wizard configuration
export const stepMetadata: StepMetadata = {
  title: 'Positions Monitoring',
  subtitle: 'Compare bank positions against Illio platform positions',
};

export const PositionsMonitoringStep: React.FC<
  PositionsMonitoringStepProps
> = () => {
  const {
    positions,
    summary,
    isLoading,
    error,
    refetch,
    showOnlyRelevant,
    setShowOnlyRelevant,
    hasRelevantCodes,
    getTreeDataPath,
    autosizeColumns,
    setAutosizeColumns,
    apiRef,
    searchTerm,
    handleSearch,
  } = usePositionsMonitoringStep();

  // Get column definitions
  const columns = useMemo(() => getColumns(), []);

  // Calculate issue counts for display
  const issueCount = summary
    ? summary.missing_in_illio +
      summary.missing_in_bank +
      summary.quantity_alert +
      summary.price_alert
    : 0;

  return (
    <Box className="positionsMonitoringStep">
      {/* Search bar */}
      <Box sx={{ maxWidth: 300 }}>
        <SearchBar
          onSearch={handleSearch}
          value={searchTerm}
          placeholder="Search positions..."
        />
      </Box>

      {/* Header with summary chips */}
      <Box className="header">
        <Box className="headerActions">
          <AutosizeColumnsToggle
            checked={autosizeColumns}
            onChange={setAutosizeColumns}
          />
          {hasRelevantCodes && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={showOnlyRelevant}
                  onChange={e => setShowOnlyRelevant(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label="Show only positions in uploaded data"
            />
          )}
          <Tooltip title="Refresh positions">
            <IconButton onClick={refetch} disabled={isLoading} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Box className="summaryChips">
          {summary && <SummaryChips summary={summary} />}
        </Box>
      </Box>

      {/* Error message */}
      {error && (
        <Alert severity="error" className="errorAlert">
          {error}
        </Alert>
      )}

      {/* Status summary */}
      {summary && issueCount === 0 && !isLoading && (
        <Alert severity="success" className="successAlert">
          <Typography variant="body2">
            All positions are matched within tolerance thresholds.
          </Typography>
        </Alert>
      )}

      {/* Data Grid */}
      <Box className="dataGridContainer">
        <StyledDataGrid
          apiRef={apiRef}
          loading={isLoading}
          rows={positions}
          columns={columns}
          columnGroupingModel={columnGroupingModel}
          getRowId={row => row.id}
          disableRowSelectionOnClick
          hideFooter
          treeData
          getTreeDataPath={getTreeDataPath}
          groupingColDef={{
            headerName: 'Portfolio',
            width: 250,
            hideDescendantCount: true,
            valueGetter: (_value, row) => {
              if (row.isGroup) {
                return row.portfolio_name;
              }
              return row.asset_name;
            },
          }}
          defaultGroupingExpansionDepth={1}
          initialState={{
            pagination: { paginationModel: { pageSize: 100 } },
          }}
          getRowClassName={params => {
            if (params.row.isGroup) return 'portfolioGroupRow';
            if (params.row.status === 'matched') return 'matchedRow';
            if (
              params.row.status === 'missing_in_illio' ||
              params.row.status === 'missing_in_bank'
            )
              return 'missingRow';
            return 'alertRow';
          }}
        />
      </Box>
    </Box>
  );
};

export { stepMetadata as positionsMonitoringMetadata };
